import React, { useEffect } from 'react';
import { Box, Typography, Container, Divider, useTheme } from '@mui/material';

export default function Privacy() {
  const theme = useTheme();

  useEffect(() => {
    document.title = "Privacy Notice | CoreTechTalks";
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ fontWeight: 800, mb: 2, fontFamily: '"Outfit", sans-serif' }}
        >
          Privacy Notice
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Effective Date: July 1, 2026
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            1. Introduction & Overview
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            Welcome to CoreTechTalks ("we," "our," or "us"). We operate CoreTechTalks.com, a platform providing technical publications, software architecture tutorials, and free web utilities.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Your privacy is highly important to us. This Privacy Notice outlines our data collection, security measures, and privacy policies regarding visitors of our website and users of our utility applications.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            2. Our Zero-Upload Local Processing Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            For all tools hosted on our platform, including document analysis modules (e.g. the Rent Agreement Red Flag Detector) and PDF manipulation suites:
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, pl: 3, borderLeft: `3px solid ${theme.palette.primary.main}`, mb: 2 }}>
            <strong>Local Processing:</strong> All documents, files, and agreements you upload for auditing, filtering, or compiling are processed exclusively inside temporary memory locally within your web browser context.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            We do not transmit, upload, or save your documents to any remote servers. Once you close your browser tab or session, the data is entirely purged from memory.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            3. Data We Collect
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            We do not require user accounts or registration to access our technical blogs or utilize our applications. We only log minimal, non-identifiable technical data:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: 'text.secondary', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <li><strong>Usage Logs:</strong> Standard web analytics details such as pages visited, timestamps, click flows, and browser type.</li>
            <li><strong>Technical Info:</strong> Anonymous device dimensions, network routing descriptors, and general geographic location data (country level).</li>
            <li><strong>Contact Details:</strong> If you subscribe to our newsletter or send us an inquiry, we securely store your email address and any text communication you share.</li>
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            4. Cookies & Trackers
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            CoreTechTalks uses minimal cookies to improve site performance and save interface configurations (such as your light/dark mode preference).
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            You can configure your browser to decline all cookies or to alert you when cookies are being sent. Note that some portions of our services may not function optimally if cookies are disabled.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            5. Third-Party Services
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            We leverage reliable third-party infrastructure providers to host and secure our platform:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: 'text.secondary', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <li><strong>Hosting:</strong> Firebase Hosting is used to store and deliver static assets securely.</li>
            <li><strong>Analytics:</strong> Google Analytics may be used to analyze general traffic patterns and identify optimization opportunities.</li>
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            6. Policy Updates & Contact Information
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            We reserve the right to modify this Privacy Notice at any time. Any changes will be posted on this page with an updated effective date.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            If you have questions, feedback, or concerns regarding your privacy or data safety, feel free to contact us at <strong>contact@coretechtalks.com</strong>.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
